import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import {
  buildTaskColumns,
  type TaskRenderColumn,
  type TaskRenderColumnSimple,
} from "@src/components/modules/resource-planning/tasks/listing/builders";
import type { AppStore } from "@src/stores/AppStore";
import { pxToNumber } from "@src/utils/pxToNumber";
import { debounce } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

export class TaskListingColumnsStore {
  private readonly CACHE_KEY = "task-listing-columns";

  @observable.ref
  draggedColumn: TaskRenderColumnSimple | undefined = undefined;

  @observable.ref
  resizingColumnKey: string | null = null;

  @observable.ref
  resizingColumnNewWidth: number | null = null;

  @observable
  private columns: TaskRenderColumn[] = [];

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @action.bound
  generateColumns() {
    const columns = buildTaskColumns(
      this.appStore.authStore.isInternalUser,
      this.appStore.tasksListingStore.handleCopyId,
      this.appStore.workspaceStore,
    );
    const cachedColumns = this.getColumnsFromCache();

    if (cachedColumns) {
      for (const { key, width, isVisible, order } of cachedColumns) {
        const matchingColumnIndex = columns.findIndex(
          (column) => column.key === key,
        );
        if (matchingColumnIndex === -1) continue;
        columns[matchingColumnIndex].width = width;
        columns[matchingColumnIndex].isVisible = isVisible;
        columns[matchingColumnIndex].order = order;
      }
    }

    this.columns = columns;
    this.columns.sort((a, b) => a.order - b.order);
  }

  @computed
  get titleColumn(): TaskRenderColumn | undefined {
    return this.columns.find(({ key }) => key === "title");
  }

  @computed
  get columnsToControl(): TaskRenderColumn[] {
    return this.columns.filter(
      ({ disableVisibilityControl }) => !disableVisibilityControl,
    );
  }

  @computed
  get columnsToRender(): TaskRenderColumn[] {
    return this.columns.filter(
      ({ key, isVisible }) => key !== "title" && isVisible,
    );
  }

  @computed
  get columnKeysToRender(): string[] {
    return this.columnsToRender.map(({ key }) => key);
  }

  @action.bound
  toggleColumnVisibility(columnKey: string, isVisible: boolean): void {
    for (const column of this.columns) {
      if (column.key !== columnKey) continue;
      column.isVisible = isVisible;
      break;
    }
    this.saveColumnsToCache();
  }

  private saveColumnsToCache = debounce(() => {
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(this.columns));
  }, 500);

  @action.bound
  private getColumnsFromCache(): TaskRenderColumn[] | null {
    const columns = localStorage.getItem(this.CACHE_KEY);
    if (!columns) return null;
    return JSON.parse(columns);
  }

  @action.bound
  handleResetColumns(): void {
    localStorage.removeItem(this.CACHE_KEY);
    this.generateColumns();
  }

  @action.bound
  handleColumnResizeStart(columnKey: string) {
    this.resizingColumnKey = columnKey;
  }

  @action.bound
  handleColumnResize(newWidth: string) {
    if (!this.resizingColumnKey) return;
    this.resizingColumnNewWidth = pxToNumber(newWidth);
  }

  @action.bound
  handleColumnResizeStop() {
    if (!this.resizingColumnKey || !this.resizingColumnNewWidth) return;

    for (const column of this.columns) {
      if (column.key !== this.resizingColumnKey) continue;
      column.width = this.resizingColumnNewWidth;
      break;
    }

    this.resizingColumnKey = null;
    this.resizingColumnNewWidth = null;
    this.saveColumnsToCache();
  }

  @action.bound
  handleReorderStart({ active }: DragStartEvent) {
    if (!active.data.current) return;
    if (!("key" in active.data.current)) return;
    if (!("width" in active.data.current)) return;
    if (!("title" in active.data.current)) return;
    this.draggedColumn = active.data.current as TaskRenderColumnSimple;
  }

  @action.bound
  handleReorderEnd({ active, over }: DragEndEvent) {
    if (!active.id || !over?.id || active.id === over.id) return;

    const oldIndex = this.columns.findIndex(({ key }) => key === active.id);
    const newIndex = this.columns.findIndex(({ key }) => key === over.id);
    this.columns = arrayMove(this.columns, oldIndex, newIndex).map(
      (column, index) => ({
        ...column,
        order: index,
      }),
    );
    this.saveColumnsToCache();
  }
}
